/**
 * Mark a conversation read or unread for the caller (TRUS-759): from a swipe on
 * a Members / Team row, or the row's button / menu item.
 *
 * Read = POST .../channel/{id}/read (up to the newest message). Unread = POST
 * .../channel/{id}/unread. That one answers `applied: false` when the caller
 * has no channel row (a member DM they read through the team inbox), and an
 * older backend answers 404/405: both say so instead of pretending.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { messagingApi } from '@/services/messagingApi';
import { toast } from '@/components/ui/use-toast';
import { errorStatus } from '../errorDetail';
import { MEMBER_INBOX_KEY } from '../inbox/memberInbox';

export interface ReadToggleTarget {
  channelId: number;
  /** Currently unread for the caller -> the action marks it read. */
  unread: boolean;
}

export class NotApplied extends Error {}

export function useConversationReadToggle() {
  const { t } = useAppTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ channelId, unread }: ReadToggleTarget) => {
      if (unread) {
        await messagingApi.markChannelRead(channelId);
        return;
      }
      const result = await messagingApi.markChannelUnread(channelId);
      if (result && result.applied === false) throw new NotApplied();
    },
    onError: (error: Error, { unread }) => {
      const status = errorStatus(error);
      toast({
        title: unread
          ? t('messaging.readState.markReadFailed', 'Could not mark as read')
          : error instanceof NotApplied || status === 404 || status === 405
            ? t('messaging.readState.cannotMarkUnread', "Can't mark this conversation unread")
            : t('messaging.readState.markUnreadFailed', 'Could not mark as unread'),
        variant: 'destructive',
      });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: [MEMBER_INBOX_KEY] });
      void qc.invalidateQueries({ queryKey: ['messaging-channels'] });
    },
  });
}
